import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, User, Mail, Briefcase, MapPin, Search, Loader2,
  CheckSquare, Square, ChevronDown, StickyNote, Calendar,
  ExternalLink, X, FileText, Sparkles, GripVertical, Filter, Video,
  MessageSquare
} from 'lucide-react';
import { InterviewScheduleModal } from '../components/InterviewScheduleModal';

const STAGES = [
  { key: 'pending',     label: 'Applied',      color: 'bg-blue-50 border-blue-200',   badge: 'bg-blue-100 text-blue-700',     dot: 'bg-blue-500' },
  { key: 'reviewed',    label: 'Under Review', color: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-700',   dot: 'bg-amber-500' },
  { key: 'shortlisted', label: 'Shortlisted',  color: 'bg-purple-50 border-purple-200',badge:'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
  { key: 'interview',   label: 'Interview',    color: 'bg-indigo-50 border-indigo-200',badge:'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500' },
  { key: 'hired',       label: 'Hired',        color: 'bg-emerald-50 border-emerald-200',badge:'bg-emerald-100 text-emerald-700',dot:'bg-emerald-500' },
  { key: 'rejected',    label: 'Rejected',     color: 'bg-red-50 border-red-200',     badge: 'bg-red-100 text-red-700',       dot: 'bg-red-400' },
];

const STAGE_KEYS = STAGES.map(s => s.key);

export default function CandidatePipelinePage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [jobFilter, setJobFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [dragging, setDragging] = useState(null); // { appId, fromStage }
  const [dragOver, setDragOver] = useState(null); // stage key
  const dragCounterRef = useRef({}); // per-column enter count to avoid child-element flicker
  const [detailApp, setDetailApp] = useState(null); // app object for side panel
  const [schedulingApp, setSchedulingApp] = useState(null);
  const [creatingRoom, setCreatingRoom] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);

  useEffect(() => { fetchPipeline(); }, []);

  const fetchPipeline = async () => {
    try {
      const res = await api.get('/applications/employer/pipeline');
      setApplications(res.data);
    } catch {
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  };

  // ---- Derived data ----
  const jobs = [...new Map(
    applications.map(a => [a.job_id, { id: a.job_id, title: a.job_title }])
  ).values()];

  const filtered = applications.filter(app => {
    const matchJob = jobFilter === 'all' || app.job_id === jobFilter;
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (app.applicant?.full_name || '').toLowerCase().includes(q) ||
      (app.applicant?.email || '').toLowerCase().includes(q) ||
      (app.job_title || '').toLowerCase().includes(q);
    return matchJob && matchSearch;
  });

  const columns = STAGES.reduce((acc, s) => {
    acc[s.key] = filtered.filter(a => {
      const norm = a.status === 'reviewing' ? 'reviewed' : a.status === 'offered' ? 'hired' : a.status;
      return norm === s.key;
    });
    return acc;
  }, {});

  // ---- Drag & Drop ----
  const handleDragStart = (e, appId, fromStage, name) => {
    setDragging({ appId, fromStage });
    e.dataTransfer.effectAllowed = 'move';
    // Custom drag ghost
    const ghost = document.createElement('div');
    ghost.textContent = name;
    ghost.style.cssText = 'position:absolute;top:-9999px;left:-9999px;padding:8px 14px;background:#003366;color:#fff;font-size:13px;font-weight:600;border-radius:8px;pointer-events:none;white-space:nowrap;box-shadow:0 4px 12px rgba(0,0,0,.25)';
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, ghost.offsetWidth / 2, 20);
    setTimeout(() => document.body.removeChild(ghost), 0);
  };

  const handleDragEnter = (e, stage) => {
    e.preventDefault();
    dragCounterRef.current[stage] = (dragCounterRef.current[stage] || 0) + 1;
    setDragOver(stage);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = (stage) => {
    dragCounterRef.current[stage] = (dragCounterRef.current[stage] || 1) - 1;
    if (dragCounterRef.current[stage] <= 0) {
      dragCounterRef.current[stage] = 0;
      setDragOver(null);
    }
  };

  const handleDragEnd = () => {
    setDragging(null);
    setDragOver(null);
    dragCounterRef.current = {};
  };

  const handleDrop = async (e, toStage) => {
    e.preventDefault();
    dragCounterRef.current[toStage] = 0;
    setDragOver(null);
    if (!dragging || dragging.fromStage === toStage) { setDragging(null); return; }
    const { appId } = dragging;
    setDragging(null);
    // Optimistic update
    setApplications(prev => prev.map(a => a.id === appId ? { ...a, status: toStage } : a));
    try {
      await api.put(`/applications/${appId}/status`, { status: toStage });
      toast.success('Stage updated');
    } catch {
      toast.error('Failed to update stage');
      fetchPipeline(); // revert
    }
  };

  // ---- Selection ----
  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = (stage) => {
    const ids = columns[stage].map(a => a.id);
    setSelected(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  // ---- Bulk status ----
  const applyBulkStatus = async () => {
    if (!bulkStatus || selected.size === 0) return;
    setBulkLoading(true);
    try {
      const res = await api.put('/applications/bulk-status', {
        application_ids: [...selected],
        status: bulkStatus
      });
      toast.success(`Updated ${res.data.updated} applications`);
      setSelected(new Set());
      setBulkStatus('');
      fetchPipeline();
    } catch {
      toast.error('Bulk update failed');
    } finally {
      setBulkLoading(false);
    }
  };

  // ---- Detail panel ----
  const openDetail = async (app) => {
    setDetailApp(app);
    setNotes([]);
    setNoteText('');
    setLoadingNotes(true);
    try {
      const res = await api.get(`/applications/${app.id}/notes`);
      setNotes(res.data);
    } catch {
      // ignore
    } finally {
      setLoadingNotes(false);
    }
  };

  const submitNote = async () => {
    if (!noteText.trim() || !detailApp) return;
    setSavingNote(true);
    try {
      const res = await api.post(`/applications/${detailApp.id}/notes`, { text: noteText.trim() });
      setNotes(prev => [...prev, res.data]);
      setNoteText('');
    } catch {
      toast.error('Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  const updateDetailStatus = async (newStatus) => {
    try {
      await api.put(`/applications/${detailApp.id}/status`, { status: newStatus });
      setDetailApp(prev => ({ ...prev, status: newStatus }));
      setApplications(prev => prev.map(a => a.id === detailApp.id ? { ...a, status: newStatus } : a));
      toast.success('Status updated');
    } catch {
      toast.error('Failed to update status');
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F0F4F8]">
      <Loader2 className="w-8 h-8 animate-spin text-[#003366]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F0F4F8] flex flex-col">
      {/* Header */}
      <div className="bg-[#003366] text-white px-6 py-4 flex items-center gap-4 shrink-0">
        <button onClick={() => navigate('/employer/dashboard')} className="flex items-center gap-1.5 text-white/70 hover:text-white transition-colors text-sm">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </button>
        <div className="h-4 w-px bg-white/20" />
        <h1 className="text-lg font-bold flex-1">Candidate Pipeline</h1>
        <span className="text-white/60 text-sm">{applications.length} total</span>
      </div>

      {/* Toolbar */}
      <div className="bg-white border-b px-6 py-3 flex flex-wrap items-center gap-3 shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input className="pl-8 w-56 h-8 text-sm" placeholder="Search candidates..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select value={jobFilter} onValueChange={setJobFilter}>
          <SelectTrigger className="h-8 w-44 text-sm">
            <Filter className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
            <SelectValue placeholder="All Jobs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Jobs</SelectItem>
            {jobs.map(j => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
          </SelectContent>
        </Select>

        {selected.size > 0 && (
          <>
            <div className="h-4 w-px bg-border" />
            <span className="text-sm text-muted-foreground">{selected.size} selected</span>
            <Select value={bulkStatus} onValueChange={setBulkStatus}>
              <SelectTrigger className="h-8 w-40 text-sm">
                <SelectValue placeholder="Move to..." />
              </SelectTrigger>
              <SelectContent>
                {STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" className="h-8 bg-[#003366] hover:bg-[#003366]/90" onClick={applyBulkStatus} disabled={bulkLoading || !bulkStatus}>
              {bulkLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Apply'}
            </Button>
            <button className="text-xs text-muted-foreground hover:text-foreground" onClick={clearSelection}>Clear</button>
          </>
        )}

        <div className="ml-auto text-xs text-muted-foreground">
          {filtered.length} candidate{filtered.length !== 1 ? 's' : ''} shown
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto px-4 py-4">
        <div className="flex gap-3 h-full min-w-max pb-4">
          {STAGES.map(stage => (
            <div
              key={stage.key}
              className={`w-72 flex flex-col rounded-xl border-2 transition-all duration-150 ${
                dragOver === stage.key
                  ? 'border-[#003366] bg-[#003366]/5 shadow-lg scale-[1.015]'
                  : stage.color
              }`}
              onDragEnter={(e) => handleDragEnter(e, stage.key)}
              onDragOver={handleDragOver}
              onDragLeave={() => handleDragLeave(stage.key)}
              onDrop={(e) => handleDrop(e, stage.key)}
            >
              {/* Column header */}
              <div className="px-3 py-2.5 flex items-center justify-between border-b border-inherit shrink-0">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${stage.dot}`} />
                  <span className="font-semibold text-sm text-foreground">{stage.label}</span>
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${stage.badge}`}>
                    {columns[stage.key]?.length || 0}
                  </span>
                </div>
                {columns[stage.key]?.length > 0 && (
                  <button
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => selectAll(stage.key)}
                    title="Select all in column"
                  >
                    <CheckSquare className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Cards */}
              <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[120px]">
                {columns[stage.key]?.length === 0 && (
                  <div className={`flex items-center justify-center h-20 text-xs rounded-lg border-2 border-dashed transition-colors ${
                    dragOver === stage.key && dragging
                      ? 'border-[#003366]/60 text-[#003366]/60 bg-[#003366]/5'
                      : 'border-gray-200 text-muted-foreground/40'
                  }`}>
                    {dragOver === stage.key && dragging ? '↓ Release to drop' : 'Drop here'}
                  </div>
                )}
                {columns[stage.key]?.map(app => (
                  <CandidateCard
                    key={app.id}
                    app={app}
                    stage={stage}
                    isSelected={selected.has(app.id)}
                    onToggleSelect={() => toggleSelect(app.id)}
                    onDragStart={(e) => handleDragStart(e, app.id, stage.key, app.applicant?.full_name || 'Candidate')}
                    onDragEnd={handleDragEnd}
                    onOpenDetail={() => openDetail(app)}
                    isDragging={dragging?.appId === app.id}
                  />
                ))}
                {/* Drop placeholder at bottom of non-empty columns */}
                {columns[stage.key]?.length > 0 && dragOver === stage.key && dragging && dragging.fromStage !== stage.key && (
                  <div className="h-14 border-2 border-dashed border-[#003366]/40 rounded-lg bg-[#003366]/5 flex items-center justify-center text-xs text-[#003366]/60 font-medium">
                    ↓ Drop here
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Interview scheduling modal */}
      {schedulingApp && (
        <InterviewScheduleModal
          app={schedulingApp}
          onClose={() => setSchedulingApp(null)}
          onScheduled={(updated) => {
            setApplications(prev => prev.map(a => a.id === updated.id ? { ...a, ...updated } : a));
            setDetailApp(prev => prev?.id === updated.id ? { ...prev, ...updated } : prev);
            setSchedulingApp(null);
          }}
        />
      )}

      {/* Side Detail Panel */}
      {detailApp && (
        <DetailPanel
          app={detailApp}
          notes={notes}
          loadingNotes={loadingNotes}
          noteText={noteText}
          setNoteText={setNoteText}
          savingNote={savingNote}
          onSubmitNote={submitNote}
          onUpdateStatus={updateDetailStatus}
          onClose={() => setDetailApp(null)}
          onScheduleInterview={() => setSchedulingApp(detailApp)}
          onStartVideo={async () => {
            setCreatingRoom(true);
            try {
              const res = await api.post('/video/rooms', { application_id: detailApp.id });
              navigate(`/interview/${res.data.id}`);
            } catch { toast.error('Failed to create video room'); }
            finally { setCreatingRoom(false); }
          }}
          creatingRoom={creatingRoom}
        />
      )}
    </div>
  );
}

// ---- Candidate Card ----
function CandidateCard({ app, stage, isSelected, onToggleSelect, onDragStart, onDragEnd, onOpenDetail, isDragging }) {
  const initials = (app.applicant?.full_name || 'U').charAt(0).toUpperCase();
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`bg-white rounded-lg border p-3 cursor-grab active:cursor-grabbing shadow-sm transition-all select-none ${
        isDragging ? 'opacity-30 scale-95 shadow-none' : 'hover:shadow-md'
      } ${isSelected ? 'ring-2 ring-[#003366]' : ''}`}
    >
      <div className="flex items-start gap-2">
        <button
          className="mt-0.5 shrink-0 text-muted-foreground hover:text-[#003366] transition-colors"
          onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
        >
          {isSelected
            ? <CheckSquare className="w-4 h-4 text-[#003366]" />
            : <Square className="w-4 h-4" />
          }
        </button>
        <div
          className="w-8 h-8 rounded-full bg-[#003366] flex items-center justify-center shrink-0 text-white text-xs font-bold"
        >
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <button
            className="font-semibold text-sm text-foreground hover:text-[#003366] transition-colors text-left line-clamp-1 w-full"
            onClick={onOpenDetail}
          >
            {app.applicant?.full_name || 'Unknown'}
          </button>
          <p className="text-xs text-muted-foreground truncate">{app.applicant?.email}</p>
        </div>
        <GripVertical className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0 mt-0.5" />
      </div>

      {/* Job */}
      <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
        <Briefcase className="w-3 h-3 shrink-0" />
        <span className="truncate">{app.job_title}</span>
      </div>

      {/* AI score */}
      {app.ai_match_score && (
        <div className="mt-1.5 flex items-center gap-1">
          <span className="text-xs bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded font-medium">
            <Sparkles className="w-2.5 h-2.5 inline mr-0.5" />{app.ai_match_score}%
          </span>
        </div>
      )}

      {/* Skills */}
      {app.applicant?.skills?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {app.applicant.skills.slice(0, 3).map((s, i) => (
            <span key={i} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{s}</span>
          ))}
          {app.applicant.skills.length > 3 && (
            <span className="text-xs text-muted-foreground">+{app.applicant.skills.length - 3}</span>
          )}
        </div>
      )}

      {/* Applied date */}
      <div className="mt-2 flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {new Date(app.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
        </span>
        <button
          className="text-xs text-[#003366] hover:underline font-medium"
          onClick={onOpenDetail}
        >
          View
        </button>
      </div>
    </div>
  );
}

// ---- Detail Side Panel ----
function DetailPanel({ app, notes, loadingNotes, noteText, setNoteText, savingNote, onSubmitNote, onUpdateStatus, onClose, onScheduleInterview, onStartVideo, creatingRoom }) {
  const textRef = useRef(null);

  return (
    <div className="fixed top-0 right-0 bottom-0 w-96 bg-white border-l shadow-2xl flex flex-col z-50">
      {/* Panel header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-[#003366] text-white shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center font-bold text-sm">
            {(app.applicant?.full_name || 'U').charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="font-semibold text-sm leading-tight">{app.applicant?.full_name || 'Unknown'}</p>
            <p className="text-xs text-white/70">{app.job_title}</p>
          </div>
        </div>
        <button onClick={onClose} className="text-white/70 hover:text-white transition-colors p-1">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Contact */}
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Contact</p>
          <div className="flex items-center gap-2 text-sm">
            <Mail className="w-3.5 h-3.5 text-muted-foreground" />
            <a href={`mailto:${app.applicant?.email}`} className="text-[#003366] hover:underline truncate">
              {app.applicant?.email}
            </a>
          </div>
          {app.location && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="w-3.5 h-3.5" />{app.location}
            </div>
          )}
          {app.applicant?.experience_years && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Briefcase className="w-3.5 h-3.5" />{app.applicant.experience_years} years experience
            </div>
          )}
        </div>

        {/* Status */}
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Move Stage</p>
          <div className="grid grid-cols-2 gap-1.5">
            {STAGES.map(s => (
              <button
                key={s.key}
                onClick={() => onUpdateStatus(s.key)}
                className={`text-xs py-1.5 px-2 rounded-lg border font-medium transition-all ${
                  (app.status === s.key || (app.status === 'reviewing' && s.key === 'reviewed') || (app.status === 'offered' && s.key === 'hired'))
                    ? `${s.badge} border-transparent`
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* CV */}
        {app.applicant?.cv_url && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Documents</p>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}${app.applicant.cv_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-[#003366] hover:underline"
            >
              <FileText className="w-4 h-4" />View CV <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}

        {/* Cover letter */}
        {app.cover_letter && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Cover Letter</p>
            <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 border max-h-28 overflow-y-auto leading-relaxed">
              {app.cover_letter}
            </div>
          </div>
        )}

        {/* Skills */}
        {app.applicant?.skills?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Skills</p>
            <div className="flex flex-wrap gap-1.5">
              {app.applicant.skills.map((s, i) => (
                <span key={i} className="text-xs bg-[#003366]/10 text-[#003366] px-2 py-0.5 rounded-full">{s}</span>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Internal Notes</p>
          {loadingNotes ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
              <Loader2 className="w-3 h-3 animate-spin" />Loading notes…
            </div>
          ) : notes.length === 0 ? (
            <p className="text-xs text-muted-foreground italic py-1">No notes yet.</p>
          ) : (
            <div className="space-y-2 mb-3">
              {notes.map(note => (
                <div key={note.id} className="bg-amber-50 border border-amber-100 rounded-lg p-2.5">
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{note.text}</p>
                  <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
                    <span className="font-medium text-amber-700">{note.author_name}</span>
                    <span>{new Date(note.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <textarea
            ref={textRef}
            className="w-full text-sm border rounded-lg p-2.5 resize-none focus:outline-none focus:ring-1 focus:ring-[#003366] bg-white"
            rows={3}
            placeholder="Add a note (visible only to your team)…"
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) onSubmitNote(); }}
          />
          <button
            onClick={onSubmitNote}
            disabled={savingNote || !noteText.trim()}
            className="mt-1.5 w-full bg-[#003366] text-white text-sm py-1.5 rounded-lg hover:bg-[#003366]/90 disabled:opacity-40 transition-colors flex items-center justify-center gap-1.5"
          >
            {savingNote ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <StickyNote className="w-3.5 h-3.5" />}
            Save Note
          </button>
        </div>

        {/* Actions */}
        <div className="space-y-2 pt-2 border-t">
          <button
            onClick={onScheduleInterview}
            className="w-full flex items-center justify-center gap-2 text-sm bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 py-2 rounded-xl font-medium transition-colors"
          >
            <Calendar className="w-4 h-4" /> Schedule Interview
          </button>
          <button
            onClick={onStartVideo}
            disabled={creatingRoom}
            className="w-full flex items-center justify-center gap-2 text-sm bg-[#003366]/10 text-[#003366] hover:bg-[#003366]/20 py-2 rounded-xl font-medium transition-colors disabled:opacity-50"
          >
            {creatingRoom ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}
            Start Video Interview
          </button>
          <Link
            to={`/messages?with=${app.applicant_id || app.applicant?.id}&context=${encodeURIComponent((app.job_title || '') + ' application')}&context_id=${app.id}`}
            className="w-full flex items-center justify-center gap-2 text-sm bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 py-2 rounded-xl font-medium transition-colors"
          >
            <MessageSquare className="w-4 h-4" /> Message Candidate
          </Link>
          <Link
            to={`/employer/jobs/${app.job_id}/applicants`}
            className="flex items-center justify-center gap-1.5 text-sm text-[#003366] hover:underline font-medium py-1"
          >
            View all applicants <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
